package dev.danko.roblox_craft_ai.components

import dev.danko.roblox_craft_ai.state.Txt
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.client.RobloxCraftApiClient
import dev.danko.roblox_craft_ai.shared.model.HealthComponent
import dev.danko.roblox_craft_ai.shared.model.HealthConstants
import dev.danko.roblox_craft_ai.shared.model.HealthResponse
import dev.danko.roblox_craft_ai.uikit.components.AppButton
import dev.danko.roblox_craft_ai.uikit.components.AppButtonSize
import dev.danko.roblox_craft_ai.uikit.components.AppButtonVariant
import dev.danko.roblox_craft_ai.uikit.components.ModalDialog
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlin.js.Date
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import kotlin.math.floor

private val DEGRADED_PRIORITY = listOf(
    "llm.registry",
    "llm.glm-5.3-flash",
    "llm.gemini-3.7",
    "luau.analyzer",
    "storage.disk"
)

private val COMPONENT_ICONS = mapOf(
    "liveness" to "💓",
    "llm.glm-5.3-flash" to "🧠",
    "llm.gemini-3.7" to "🔮",
    "llm.registry" to "📦",
    "luau.analyzer" to "🔍",
    "storage.disk" to "💾"
)

private fun statusIcon(status: String): String = when (status) {
    HealthConstants.STATUS_OK -> "💚"
    HealthConstants.STATUS_DEGRADED -> "🟡"
    HealthConstants.STATUS_DOWN -> "🔴"
    else -> "⚪"
}

private fun formatTime(tsMs: Long): String {
    val d = Date(tsMs.toDouble())
    val hh = d.getHours().toString().padStart(2, '0')
    val mm = d.getMinutes().toString().padStart(2, '0')
    val ss = d.getSeconds().toString().padStart(2, '0')
    return "$hh:$mm:$ss"
}

private fun firstDegradedMessage(components: Map<String, HealthComponent>): Pair<String, String>? {
    for (key in DEGRADED_PRIORITY) {
        val c = components[key] ?: continue
        if (c.status == HealthConstants.STATUS_DEGRADED || c.status == HealthConstants.STATUS_DOWN) {
            val shortName = when (key) {
                "llm.registry" -> "LLM Registry"
                "llm.glm-5.3-flash" -> "GLM"
                "llm.gemini-3.7" -> "Gemini"
                "luau.analyzer" -> "Luau Analyzer"
                "storage.disk" -> "Disk"
                else -> key
            }
            val detail = c.message?.take(40) ?: shortName
            return shortName to detail
        }
    }
    return null
}

@Composable
fun HealthStatusBadge() {
    val theme = LocalAppTheme.current
    val scope = rememberCoroutineScope()
    var healthResponse by remember { mutableStateOf<HealthResponse?>(null) }
    var showModal by remember { mutableStateOf(false) }
    var lastUpdatedLocal by remember { mutableStateOf<Long?>(null) }

    LaunchedEffect(Unit) {
        while (isActive) {
            try {
                val result = RobloxCraftApiClient.getHealth()
                if (result != null) {
                    healthResponse = result
                    lastUpdatedLocal = Date.now().toLong()
                }
            } catch (_: Throwable) {}
            delay(60_000L)
        }
    }

    val overall = healthResponse?.status
    val (badgeText, badgeColor) = when {
        healthResponse == null -> Txt.health.unknown to theme.colors.textSecondary
        overall == HealthConstants.STATUS_OK -> Txt.health.allOk to theme.colors.statusReady
        overall == HealthConstants.STATUS_DOWN -> Txt.health.critical to theme.colors.statusFailed
        else -> {
            val degraded = healthResponse?.components?.let { firstDegradedMessage(it) }
            if (degraded != null) {
                Txt.health.degraded(degraded.first) to theme.colors.statusPending
            } else {
                Txt.health.degradedGeneric to theme.colors.statusPending
            }
        }
    }

    Row(
        modifier = Modifier
            .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
            .borderRadius(9999.px)
            .backgroundColor(theme.colors.surfaceElevated)
            .border(1.px, LineStyle.Solid, theme.colors.border)
            .gap(0.375.cssRem)
            .cursor(Cursor.Pointer)
            .onClick { showModal = true },
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            badgeText,
            Modifier
                .fontSize(0.8.cssRem)
                .color(badgeColor)
        )
    }

    ModalDialog(
        visible = showModal,
        onCloseRequest = { showModal = false },
        title = Txt.health.title,
        footer = {
            AppButton(
                text = Txt.common.close,
                variant = AppButtonVariant.PRIMARY,
                size = AppButtonSize.MD,
                onClick = { showModal = false }
            )
        }
    ) {
        val components = healthResponse?.components ?: emptyMap()
        val orderedKeys = HealthConstants.COMPONENT_NAMES
        Column(modifier = Modifier.fillMaxWidth().gap(0.5.cssRem)) {
            if (lastUpdatedLocal != null) {
                SpanText(
                    Txt.health.updatedAt(formatTime(lastUpdatedLocal!!)),
                    Modifier.fontSize(0.8.cssRem).color(theme.colors.textSecondary)
                )
            }
            for (key in orderedKeys) {
                val c = components[key]
                if (c == null) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(topBottom = 0.375.cssRem),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.gap(0.5.cssRem)
                        ) {
                            SpanText("⚪")
                            SpanText(
                                key,
                                Modifier.fontSize(0.875.cssRem).color(theme.colors.textSecondary)
                            )
                        }
                        SpanText(
                            "—",
                            Modifier.fontSize(0.8.cssRem).color(theme.colors.textSecondary)
                        )
                    }
                    continue
                }
                val icon = COMPONENT_ICONS[key] ?: statusIcon(c.status)
                val latencyText = c.latency_ms?.let { "${it}ms" } ?: ""
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(topBottom = 0.375.cssRem)
                        .gap(0.5.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(icon, Modifier.fontSize(1.cssRem))
                    SpanText(
                        key,
                        Modifier.fontSize(0.875.cssRem).color(theme.colors.textPrimary).minWidth(11.cssRem)
                    )
                    Spacer()
                    if (latencyText.isNotBlank()) {
                        SpanText(
                            latencyText,
                            Modifier.fontSize(0.8.cssRem).color(theme.colors.textSecondary).minWidth(4.cssRem).textAlign(com.varabyte.kobweb.compose.css.TextAlign.End)
                        )
                    } else {
                        SpanText(
                            "",
                            Modifier.minWidth(4.cssRem)
                        )
                    }
                    val detail = c.message?.take(60) ?: ""
                    if (detail.isNotBlank()) {
                        SpanText(
                            detail,
                            Modifier.fontSize(0.75.cssRem).color(theme.colors.textSecondary).maxWidth(12.cssRem)
                        )
                    }
                }
            }
        }
    }
}
